/**
 * Détection des fichiers logiques de l'import dans un classeur Excel (.xlsx) :
 * chaque onglet est converti en CSV puis rapproché des fichiers *attendus* par
 * les steps sélectionnés, via le nom de l'onglet (« Élèves » → eleves), avec
 * les mêmes alias et la même normalisation que les archives ZIP.
 */

import type { LogicalFileSpec } from './stepCatalog';
import { canonicalLogicalKey, type ZipDetectionResult } from './zipBundle';
import { workbookToCsvSheets } from './xlsx';

/**
 * Lit un classeur et rapproche ses onglets des fichiers attendus. Retourne la
 * même structure que la détection ZIP (les `File` produits sont des CSV nommés
 * `<logique>.csv`, le reste de la chaîne est inchangé).
 */
export async function detectWorkbookBundle(
  workbook: File,
  expected: LogicalFileSpec[],
): Promise<ZipDetectionResult> {
  const sheets = await workbookToCsvSheets(workbook);
  const expectedByKey = new Map(expected.map((s) => [s.logicalName.toLowerCase(), s.logicalName]));

  const matched: ZipDetectionResult['matched'] = [];
  const ignored: string[] = [];
  const seen = new Set<string>();

  for (const sheet of sheets) {
    const logicalName = expectedByKey.get(canonicalLogicalKey(sheet.name));
    if (!logicalName || seen.has(logicalName)) {
      ignored.push(sheet.name);
      continue;
    }
    seen.add(logicalName);
    matched.push({
      logicalName,
      fileName: sheet.name,
      file: sheetCsvFile(sheet.csv, logicalName),
    });
  }

  const missing = expected.map((s) => s.logicalName).filter((name) => !seen.has(name));

  return { matched, ignored, missing };
}

/**
 * Mode « fichier par fichier » : extrait du classeur le CSV correspondant au
 * fichier logique demandé. Onglet retenu : celui dont le nom correspond au nom
 * logique, sinon l'onglet unique du classeur. Rejette (message FR) si le
 * classeur a plusieurs onglets et qu'aucun ne correspond.
 */
export async function workbookSheetToCsvFile(
  workbook: File,
  logicalName: string,
): Promise<File> {
  const sheets = await workbookToCsvSheets(workbook);
  const match =
    sheets.find((s) => canonicalLogicalKey(s.name) === canonicalLogicalKey(logicalName)) ??
    (sheets.length === 1 ? sheets[0] : undefined);
  if (!match) {
    throw new Error(
      `Le classeur contient ${sheets.length} onglets mais aucun nommé « ${logicalName} ». ` +
        'Renommez l’onglet à importer ou déposez le classeur complet dans l’onglet « Archive / Classeur ».',
    );
  }
  return sheetCsvFile(match.csv, logicalName);
}

/**
 * Fichiers issus d'une conversion de classeur : leur CSV est généré avec le
 * délimiteur « ; » (XLSX_CSV_DELIMITER) — la config d'import doit rester sur
 * ce délimiteur tant qu'ils sont présents.
 */
const convertedFiles = new WeakSet<File>();

export function isConvertedWorkbookFile(file: File): boolean {
  return convertedFiles.has(file);
}

function sheetCsvFile(csv: string, logicalName: string): File {
  const file = new File([csv], `${logicalName}.csv`, { type: 'text/csv' });
  convertedFiles.add(file);
  return file;
}
